/**
 * Esquemas Zod de los formularios del dominio Equipo (usuarios, contraseña, roles).
 *
 * Validan la entrada ANTES de llamar a la API (`docs/frontend_conventions.md`,
 * Validación). Los tipos de formulario se **infieren** del esquema (`z.infer`), no
 * se escriben a mano. Reflejan las reglas del backend (`application/api/domains/
 * users.py`/`roles.py`): obligatorios, contraseña de 6+ caracteres, `role_id` con
 * patrón `[a-z0-9_]`. La unicidad (username/role_id) la valida el backend; aquí no
 * se duplica.
 */
import { z } from "zod";

import { PASSWORD_MIN_LENGTH } from "./constants";

/** Mensaje de contraseña corta (reutilizado por alta y cambio de contraseña). */
const PASSWORD_SHORT_MESSAGE = `La contraseña debe tener al menos ${PASSWORD_MIN_LENGTH} caracteres.`;

/** Contraseña: obligatoria, mínimo 6 caracteres (no se recorta: los espacios cuentan). */
const passwordField = z.string().min(PASSWORD_MIN_LENGTH, PASSWORD_SHORT_MESSAGE);

// --------------------------------------------------------------------------
// Alta de usuario
// --------------------------------------------------------------------------

/**
 * Esquema de **alta de usuario** (`POST /users`). El `username` se normaliza a
 * minúsculas (igual que el SSR). `sede_id` es texto del `<select>` y se convierte
 * a número al enviar (la pantalla lo hace).
 */
export const createUserSchema = z.object({
  username: z
    .string()
    .trim()
    .min(1, "El nombre de usuario es obligatorio.")
    .transform((value) => value.toLowerCase()),
  name: z.string().trim().min(1, "El nombre es obligatorio."),
  password: passwordField,
  role: z.string().trim().min(1, "El rol es obligatorio."),
  sede_id: z.string().trim().min(1, "La sede es obligatoria."),
});

/** Valores del formulario de alta de usuario (inferidos del esquema). */
export type CreateUserValues = z.infer<typeof createUserSchema>;

// --------------------------------------------------------------------------
// Edición de usuario
// --------------------------------------------------------------------------

/**
 * Esquema de **edición de usuario** (`PATCH /users/<username>`). La contraseña es
 * OPCIONAL: vacía = no cambiar; con valor = mínimo 6 caracteres (espejo del PATCH
 * del backend, que solo aplica `password` si viene con valor).
 */
export const editUserSchema = z.object({
  username: z
    .string()
    .trim()
    .min(1, "El nombre de usuario es obligatorio.")
    .transform((value) => value.toLowerCase()),
  name: z.string().trim().min(1, "El nombre es obligatorio."),
  role: z.string().trim().min(1, "El rol es obligatorio."),
  sede_id: z.string().trim().min(1, "La sede es obligatoria."),
  password: z
    .string()
    .optional()
    .default("")
    .refine(
      (value) => value === "" || value.length >= PASSWORD_MIN_LENGTH,
      PASSWORD_SHORT_MESSAGE,
    ),
});

/** Valores del formulario de edición de usuario (inferidos del esquema). */
export type EditUserValues = z.infer<typeof editUserSchema>;

// --------------------------------------------------------------------------
// Cambio de contraseña
// --------------------------------------------------------------------------

/**
 * Esquema de **cambio de contraseña** (`POST /users/<username>/password`).
 * Contraseña de 6+ caracteres y confirmación que coincide (la confirmación es
 * solo del cliente; el backend solo recibe `password`).
 */
export const changePasswordSchema = z
  .object({
    password: passwordField,
    confirm_password: z.string(),
  })
  .superRefine((values, ctx) => {
    if (values.password !== values.confirm_password) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["confirm_password"],
        message: "Las contraseñas no coinciden.",
      });
    }
  });

/** Valores del formulario de cambio de contraseña (inferidos del esquema). */
export type ChangePasswordValues = z.infer<typeof changePasswordSchema>;

// --------------------------------------------------------------------------
// Alta de rol
// --------------------------------------------------------------------------

/** Patrón del `role_id` (igual que el backend `_ROLE_ID_PATTERN`). */
const ROLE_ID_PATTERN = /^[a-z0-9_]+$/;

/**
 * Esquema de **alta de rol** (`POST /roles`). El `role_id` se normaliza a
 * minúsculas y debe casar `[a-z0-9_]` (igual que el SSR). `permissions` se maneja
 * fuera del esquema (lista de checkboxes en estado de la pantalla).
 */
export const createRoleSchema = z.object({
  role_id: z
    .string()
    .trim()
    .min(1, "El id del rol es obligatorio.")
    .transform((value) => value.toLowerCase())
    .refine(
      (value) => ROLE_ID_PATTERN.test(value),
      "El id solo admite minúsculas, números y guion bajo.",
    ),
  name: z.string().trim().min(1, "El nombre del rol es obligatorio."),
  description: z.string().trim().optional().default(""),
});

/** Valores del formulario de alta de rol (inferidos del esquema). */
export type CreateRoleValues = z.infer<typeof createRoleSchema>;

// --------------------------------------------------------------------------
// Edición de rol
// --------------------------------------------------------------------------

/**
 * Esquema de **edición de rol** (`PATCH /roles/<role_id>`). El `role_id` no se
 * edita (solo `name`/`description`/permisos). `permissions` se maneja fuera del
 * esquema.
 */
export const editRoleSchema = z.object({
  name: z.string().trim().min(1, "El nombre del rol es obligatorio."),
  description: z.string().trim().optional().default(""),
});

/** Valores del formulario de edición de rol (inferidos del esquema). */
export type EditRoleValues = z.infer<typeof editRoleSchema>;
